import { useState, useEffect, useCallback } from 'react';

interface Location {
  latitude: number;
  longitude: number;
  error: string | null;
  loading: boolean;
  source: 'gps' | 'ip' | 'manual' | 'default' | null;
  setManualLocation: (lat: number, lng: number) => void;
  retry: () => void;
}

// Default: Edmonton, AB (where previous posts were created)
const DEFAULT_LAT = 53.4236;
const DEFAULT_LNG = -113.6262;

export const useGeolocation = (): Location => {
  const [location, setLocation] = useState<Location>({
    latitude: DEFAULT_LAT,
    longitude: DEFAULT_LNG,
    error: null,
    loading: true,
    source: null,
    setManualLocation: () => {},
    retry: () => {},
  });

  const setManualLocation = useCallback((latitude: number, longitude: number) => {
    setLocation((prev) => ({
      ...prev,
      latitude,
      longitude,
      error: null,
      loading: false,
      source: 'manual',
    }));
  }, []);

  const requestLocation = useCallback(() => {
    setLocation((prev) => ({ ...prev, loading: true, error: null }));

    if (!navigator.geolocation) {
      setLocation((prev) => ({
        ...prev,
        latitude: DEFAULT_LAT,
        longitude: DEFAULT_LNG,
        error: 'Geolocation is not supported by your browser. Using default location.',
        loading: false,
        source: 'default',
      }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation((prev) => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          error: null,
          loading: false,
          source: 'gps',
        }));
      },
      (err) => {
        let error: string;
        switch (err.code) {
          case err.PERMISSION_DENIED:
            error = 'Location permission denied. Allow location access in your browser settings to see posts near you.';
            break;
          case err.POSITION_UNAVAILABLE:
            error = 'Location unavailable. Make sure location services are enabled on your device.';
            break;
          case err.TIMEOUT:
            error = 'Location request timed out. Check your connection and try again.';
            break;
          default:
            error = 'Could not determine your location.';
        }
        setLocation((prev) => ({
          ...prev,
          latitude: DEFAULT_LAT,
          longitude: DEFAULT_LNG,
          error,
          loading: false,
          source: 'default',
        }));
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }, []);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  return { ...location, setManualLocation, retry: requestLocation };
};
